import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { fromEvent } from 'rxjs';
import { AutoSub, AutoSubOnDestroy, DeviceService, DirectionsService, MapService, MoteEventService, PositionService } from '../../providers';
import { Coords, DirectionsEventType, MapUtils, Mote, MoteEvent, MoteEventType, MotesPaths, MoteState, Point, UnlockOptions } from '../../models';
import { Marker, Map, LngLatLike } from 'mapbox-gl';

@AutoSubOnDestroy()
@Component({
    selector: 'mote-map-marker',
    templateUrl: './mote-map-marker.component.html',
    styleUrls: ['./mote-map-marker.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Default
})

export class MoteMapMarkerComponent implements OnInit, AfterViewInit {

    @ViewChild('moteMapMarkerContainer', { static: true }) moteMapMarkerContainer: ElementRef;

    @Output() moteEvent = new EventEmitter<MoteEvent>();
    @Output() dragStart = new EventEmitter<{ moteKey: string }>();
    @Output() dragEnd = new EventEmitter<{ moteKey: string, oldPosition: Coords, newPosition: Coords }>();

    @Input('mote') set setMote(mote: Mote) {
        this.mote = mote;
        this.updateMarker(mote);
    }
    @Input('selectedMoteKey') set setMoteSelected(key: string) {
        this.moteSelected = (key == this.mote.key);
    }
    @Input() markerSize: number = 80;
    @Input() enableDragging: boolean = false;
    @Input() overLayContent: any;
    @Input() hideOverlay: boolean = false;
    /** Set true to hide markers from map when clusters appear */
    @Input() clustersAreIncluded: boolean = false;
    /** Border styles: Edit (blue when selected), Tour (gold when selected), State (dynamically based on mote state) */
    @Input() style: 'edit' | 'tour' | 'state' = 'tour';

    public mote: Mote;
    public markerDiv: HTMLElement;
    public moteSelected: boolean = false;
    public moteIsUnlocking: boolean = false;
    public isThumbLoaded: boolean = false;
    public isSquareLoaded: boolean = false;
    public hideMarker: boolean = false;
    public isDirectionsShown: boolean = false;
    private mapboxMarker: Marker = null;
    private map: Map;

    // Expose to view
    public MoteState = MoteState;
    public MotesPaths = MotesPaths;

    constructor(
        private mapService: MapService,
        private deviceService: DeviceService,
        private ngZone: NgZone,
        private moteEventService: MoteEventService,
        private directionsService: DirectionsService,
        private positionService: PositionService) { }

    ngOnInit(): void {

        // Listen to motes being unlocked and perform animation
        AutoSub(this).reg['motesEventSub'] = this.moteEventService.watchMoteEvents().subscribe((ev) => {
            switch(ev.type) {
                case MoteEventType.GROUP_UNLOCK_MOTE:
                case MoteEventType.UNLOCK_MOTE:
                    // Only unlock mote that belongs to this marker
                    if(this.mote.key != ev.mote.key) break;
                    // Do not unlock locked (hence not available) motes unless forced
                    if(ev.mote.moteState != MoteState.AVAILABLE && !(ev.options as UnlockOptions)?.force) break;
                    this.animateUnlock();
                    break;
                default: break;
            }
        })

        AutoSub(this).reg['mapClickSub'] = this.mapService.watchMapClick().subscribe(ev => {
            // Do nothing on map click if this is not the active mote marker
            if(!this.moteSelected) return;
            this.clickOutside();
        });

        AutoSub(this).reg['mapZoomSub'] = this.mapService.watchMapZoom().subscribe((zoom) => {
            if(this.clustersAreIncluded) this.hideMarker = (zoom < MapUtils.DefaultClusterMaxZoom);
        })

        // Detect if directions to this mote is shown
        AutoSub(this).reg['navSub'] = this.directionsService.watchDirectionsEvents().subscribe((ev) => {
            const navigationStarted = (ev.type == DirectionsEventType.START_NAVIGATION) && (ev.mote.key == this.mote.key);
            const userPositionAvailable = Boolean(this.positionService.lastKnownPosition?.isValid);
            this.isDirectionsShown = navigationStarted && userPositionAvailable;
        })
    }

    ngAfterViewInit() {
        if(this.deviceService.isPlatformServer()) return;
    }

    private updateMarker(mote: Mote) {
        // Wait for map to init
        this.mapService.awaitInit().then(map => {
            this.map = map;
            this.markerDiv = this.moteMapMarkerContainer.nativeElement;
            this.addMarkerImage(mote);
        });
    }

    private async addMarkerImage(mote: Mote) {
        if(!mote || !mote.position || typeof mote.position.long !== "number" || typeof mote.position.lat !== "number") return;
        // Place point on map
        let point: Point = { type: 'Point', coordinates: mote.position.longLat() };
        this.addMapboxMarker(point);
        // Show image:
        // Multiply marker size with 4, because its initial scale is 0.25.
        // This is so that the marker keeps its resolution when up-scaling (to scale 1) during unlock.
        this.markerDiv.style.setProperty('width', (4 * this.markerSize)+'px');
        this.markerDiv.style.setProperty('height', (4 * this.markerSize)+'px');
    }

    private addMapboxMarker(position: Point) {
        if(!position || !position.coordinates || !this.mapService.initialized) return;
        // Remove existing marker
        if(this.mapboxMarker) this.mapboxMarker.remove();
        // Add Marker
        this.mapboxMarker = new Marker(this.markerDiv, { offset: [0, 0] });
        this.setDragging()
        this.mapboxMarker.setLngLat(<LngLatLike>position.coordinates).addTo(this.map);
    }

    private setDragging() {
        this.mapboxMarker.setDraggable(this.enableDragging);
        if(!this.enableDragging) return;

        let watchDragStart = fromEvent(this.mapboxMarker, 'dragstart');
        let watchDragEnd = fromEvent(this.mapboxMarker, 'dragend');

        AutoSub(this).reg['dragStartSub'] = watchDragStart.subscribe(ev => {
            this.dragStart.emit({ moteKey: this.mote.key });
        });

        AutoSub(this).reg['dragEndSub'] = watchDragEnd.subscribe(ev => {
            if(!this.mapboxMarker) return;
            let pos = this.mapboxMarker.getLngLat();
            let newPosition = new Coords(pos.lat, pos.lng, true);
            this.dragEnd.emit({ moteKey: this.mote.key, oldPosition: this.mote.position, newPosition: newPosition })
        });
    }

    private async animateUnlock() {
        // Start css animation
        this.moteIsUnlocking = true;
        // Wait for animation to finish (takes 1700ms)
        await new Promise<void>(resolve => setTimeout(() => resolve(), 3000));
        // Open mote page
        this.emitMoteEvent(MoteEventType.UNLOCK_DONE);
        // Make sure mote page is open before normalizing marker
        await new Promise<void>(resolve => setTimeout(() => resolve(), 1000));
        // Normalize marker
        this.moteIsUnlocking = false;
    }

    /**
     * Select mote
     */
    public markerClick() {
        // Unlock available story click
        if(this.mote.parent && this.mote.moteState == MoteState.AVAILABLE) {
            this.emitMoteEvent(MoteEventType.UNLOCK_MOTE);
            return;
        }
        // Otherwise select mote
        this.emitMoteEvent(MoteEventType.MAP_MOTE_SELECTED);
    }

    /**
     * Unselect selected mote if clicking outside marker
     */
    public clickOutside() {
        if(!this.moteSelected) return;
        this.emitMoteEvent(MoteEventType.MAP_MOTE_UNSELECTED);
    }

    private emitMoteEvent(type: MoteEventType) {
        // Always treat mote events inside of zone, even though they might stem from map event outside of zone
        this.ngZone.run(() => {
            this.moteEvent.emit({ mote: this.mote, type: type });
        });
    }

    public setThumbLoaded() {
        this.isThumbLoaded = true;
    }

    public ngOnDestroy() {
        if(this.mapboxMarker) this.mapboxMarker.remove();
    }
}
